import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useDataSource } from '@afosto/app-components-react/DataSource';
import { useOrderFormContext } from '../../OrderFormProvider';
import { FormattedMessage } from 'react-intl';
import Alert from '@material-ui/lab/Alert';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import OrderItemsList from './OrderItemsList';
import AddProductDrawer from '@afosto/app-components-react/AddProductDrawer';
import BulkProductDrawer from '@afosto/app-components-react/BulkProductDrawer';
import convertFilterToConstraint from '@afosto/utils-react/datagrid/convertFilterToConstraint';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import { items as itemsApi } from '@afosto/order/api/Items';
import translations from './translations';

const OrderItemsFormSection = ({ fetchProjection }) => {
  const intl = useIntl();
  const {
    bulkCreateItem,
    createItem,
    changeItemQuantity,
    hasProjectionError,
    order,
    setItems,
    removeItems,
    client,
    organisation,
    projection,
  } = useOrderFormContext();
  const [selectedItems, setSelectedItems] = useState([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showBulkProducts, setShowBulkProducts] = useState(false);
  const [isAddingProducts, setIsAddingProducts] = useState(false);
  const [showItemsError, setShowItemsError] = useState(false);
  const isPurchase = order?.type === 'purchase';
  const handleRefreshItemsRef = useRef(undefined);

  const fetchOrderItems = async dataSource =>
    dataSourceRequest(
      dataSource,
      async ({ filters }, requestOptions) => {
        const { fixedFilters = [] } = filters;
        const fixedConstraints = fixedFilters.reduce(
          (acc, fixedFilter) => [...acc, convertFilterToConstraint(fixedFilter)],
          [],
        );

        setShowItemsError(false);

        return itemsApi.searchGroups(
          {
            constraints: fixedConstraints,
            filters: [],
          },
          requestOptions,
        );
      },
      {
        transformData: async data => {
          try {
            let latestProjection = {};

            if (data?.length) {
              latestProjection = await fetchProjection();
            }

            const formattedProjectionItems = (latestProjection?.items ?? []).reduce(
              (acc, projectionItem) => ({ ...acc, [projectionItem?.reference]: projectionItem }),
              {},
            );
            const projectionItemReferences = Object.keys(formattedProjectionItems);

            setItems(data);

            return data.reduce((acc, item) => {
              const projectionItemReference = projectionItemReferences.find(reference =>
                (item?.ids ?? []).includes(reference),
              );
              const projectionItem = formattedProjectionItems[projectionItemReference] ?? {};
              const [firstItemId] = item?.ids ?? [];

              return [...acc, { ...item, ...projectionItem, id: firstItemId }];
            }, []);
          } catch (err) {
            setItems([]);
            setShowItemsError(true);
            return [];
          }
        },
      },
    );

  const dataSource = useDataSource(fetchOrderItems, {
    modules: ['dataProperties', 'selection', 'pagination', 'filters'],
    pagination: {
      itemsPerPage: 100,
    },
    filters: {
      fixedFilters: [
        {
          key: 'order_ids',
          operator: 'in',
          value: [order?.id],
        },
      ],
    },
    selection: {
      allowSelectAll: false,
      onAddSelection: selected => {
        setSelectedItems(oldItems => {
          const currentItemIds = oldItems.map(item => item.id);
          const newItems = selected.filter(item => !currentItemIds.includes(item.id));

          return [...oldItems, ...newItems];
        });
      },
      onRemoveSelection: selected => {
        const removedIds = selected.map(item => item.id);
        setSelectedItems(oldItems => oldItems.filter(item => !removedIds.includes(item.id)));
      },
    },
  });

  const handleCreateItem = async ({ sku, description, quantity, addAnotherProduct }) => {
    try {
      await createItem({ sku, description, quantity, isPurchase });
      dataSource.reloadDataSource();

      if (!addAnotherProduct) {
        setShowAddProduct(false);
      }
    } catch (err) {
      // Do something
    }
  };

  const handleBulkCreateItem = async data => {
    try {
      setIsAddingProducts(true);
      await bulkCreateItem({ data, isPurchase });

      dataSource.reloadDataSource();

      setShowBulkProducts(false);
    } catch (err) {
      // Do something.
    } finally {
      setIsAddingProducts(false);
    }
  };

  const handleRemoveItems = async () => {
    try {
      await removeItems(selectedItems, () => dataSource.reloadDataSource());
    } catch (err) {
      // Do something
    }
  };

  const handleItemQuantity = item => async value => {
    try {
      if (value && value !== '') {
        dataSource.setIsLoadingData(true);
        await changeItemQuantity({ item, value, data: dataSource.data, isReturn: isPurchase });
        dataSource.reloadDataSource();
      }
    } catch (err) {
      dataSource.setIsLoadingData(false);
    }
  };

  const handleRefreshItems = () => {
    dataSource.reloadDataSource();
  };

  const handleRefreshProjection = () => {
    fetchProjection();
  };

  useEffect(() => {
    handleRefreshItemsRef.current = handleRefreshItems;
  });

  useEffect(() => {
    handleRefreshItemsRef.current();
  }, [client, organisation]);

  return (
    <>
      <Card style={{ marginBottom: 8 }}>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
          action={
            <>
              <Button
                color="default"
                variant="outlined"
                onClick={() => setShowBulkProducts(true)}
                style={{ marginRight: 8 }}
              >
                <FormattedMessage {...translations.buttons.bulkAdd.label} />
              </Button>
              <Button color="primary" variant="outlined" onClick={() => setShowAddProduct(true)}>
                <FormattedMessage {...translations.buttons.add.label} />
              </Button>
            </>
          }
        />

        {showItemsError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" variant="outlined" size="small" onClick={handleRefreshItems}>
                <FormattedMessage {...translations.buttons.refresh.label} />
              </Button>
            }
          >
            <FormattedMessage {...translations.itemsErrorMessage} />
          </Alert>
        )}

        {hasProjectionError && !showItemsError && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                variant="outlined"
                size="small"
                onClick={handleRefreshProjection}
              >
                <FormattedMessage {...translations.buttons.refresh.label} />
              </Button>
            }
          >
            <FormattedMessage {...translations.projectionErrorMessage} />
          </Alert>
        )}

        {!showItemsError && (
          <OrderItemsList
            allowNegativeQuantity={!isPurchase}
            dataSource={dataSource}
            onItemQuantity={handleItemQuantity}
            onRemoveItems={handleRemoveItems}
            isIncludingVat={projection?.is_including_vat}
            isVatShifted={projection?.is_vat_shifted}
          />
        )}
      </Card>
      <AddProductDrawer
        formProps={{ allowNegativeQuantity: !isPurchase, showProductInventory: true }}
        isOpen={showAddProduct}
        onAdd={handleCreateItem}
        onClose={() => setShowAddProduct(false)}
      />
      <BulkProductDrawer
        isLoading={isAddingProducts}
        isOpen={showBulkProducts}
        onSubmit={handleBulkCreateItem}
        onClose={() => setShowBulkProducts(false)}
      />
    </>
  );
};

OrderItemsFormSection.propTypes = {
  fetchProjection: PropTypes.func,
};

OrderItemsFormSection.defaultProps = {
  fetchProjection: undefined,
};

export default OrderItemsFormSection;
